import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useSupabaseAuth } from '../context/AuthContext';
import { provisionDemo } from '../lib/demoProvisioning';
import Wordmark from '../components/Wordmark';

const PLANS = [
  { id: 'trial', label: 'Free trial (30 days)' },
  { id: 'growth', label: 'Growth — EUR 299/month after trial' },
  { id: 'enterprise', label: 'Enterprise — contact sales' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, getAccessTokenSilently } = useAuth0();
  const { profile, loading: authLoading, refreshProfile, applyProfilePatch } = useSupabaseAuth();

  const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.replace(/\/$/, '');
  const supabaseAnon = process.env.REACT_APP_SUPABASE_ANON_KEY;

  const [fullName, setFullName] = useState(() => user?.name || user?.nickname || '');
  const [orgName, setOrgName] = useState('');
  const [plan, setPlan] = useState(() => searchParams.get('plan') || 'trial');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [requiresInvitation, setRequiresInvitation] = useState(false);
  const didProvisionCheck = useRef(false);

  useEffect(() => {
    if (user?.name && !fullName) setFullName(user.name);
  }, [user, fullName]);

  useEffect(() => {
    if (authLoading) return;
    if (profile?.organisation_id) {
      navigate('/home', { replace: true });
    }
  }, [authLoading, profile, navigate]);

  useEffect(() => {
    if (authLoading || profile?.organisation_id || didProvisionCheck.current) return;
    if (!audience || !supabaseUrl || !supabaseAnon) return;

    let cancelled = false;
    didProvisionCheck.current = true;
    setCheckingExisting(true);

    (async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience },
        });
        const res = await fetch(`${supabaseUrl}/functions/v1/auth0-provision-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            apikey: supabaseAnon,
          },
          body: JSON.stringify({ mode: 'check' }),
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data?.organisation_id) {
          if (data.profile && typeof data.profile === 'object') {
            applyProfilePatch(data.profile);
          } else {
            applyProfilePatch({
              auth_subject: user?.sub || undefined,
              organisation_id: data.organisation_id,
              platform_role: 'admin',
            });
          }
          navigate('/home', { replace: true });
        }
      } catch {
        /* ignore check errors; user can still onboard manually */
      } finally {
        if (!cancelled) setCheckingExisting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    profile?.organisation_id,
    audience,
    supabaseUrl,
    supabaseAnon,
    getAccessTokenSilently,
    applyProfilePatch,
    navigate,
    user?.sub,
  ]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!orgName.trim()) {
      setError('Please enter your organisation name.');
      return;
    }
    if (!fullName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!audience || !supabaseUrl || !supabaseAnon) {
      setError('Application configuration is incomplete (Auth0 audience or Supabase URL).');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience },
      });
      const res = await fetch(`${supabaseUrl}/functions/v1/auth0-provision-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: supabaseAnon,
        },
        body: JSON.stringify({
          full_name: fullName.trim(),
          organisation_name: orgName.trim(),
          selected_plan: plan,
          email: user?.email || '',
        }),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        /* ignore */
      }
      if (!res.ok) {
        if (data?.requires_invitation) {
          setRequiresInvitation(true);
          return;
        }
        const debug = [
          data.resolved_api_key_shape ? `resolved_api_key_shape=${data.resolved_api_key_shape}` : null,
          data.diagnostics ? `diagnostics=${JSON.stringify(data.diagnostics)}` : null,
        ].filter(Boolean);
        const parts = [data.error, data.detail, data.hint, ...debug].filter(Boolean);
        const msg = parts.length ? parts.join(' — ') : raw.slice(0, 200) || `Provisioning failed (${res.status})`;
        throw new Error(msg);
      }
      const refreshed = await refreshProfile();
      if (!refreshed) {
        // Prevent home -> /onboarding bounce when DB read lags or token role mapping is delayed.
        applyProfilePatch({
          auth_subject: user?.sub || undefined,
          full_name: fullName.trim(),
          email: user?.email || '',
          organisation_id: data.organisation_id || undefined,
          platform_role: 'admin',
          selected_plan: plan,
        });
      }
      if (!data.already_provisioned && data.organisation_id) {
        try {
          await provisionDemo(getAccessTokenSilently, 'signup');
        } catch (demoErr) {
          console.warn('[Onboarding] demo provision failed:', demoErr);
        }
      }
      navigate('/home', { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not complete setup.');
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading || checkingExisting) {
    return (
      <div className="auth-page">
        <div className="auth-shell">
          <div className="auth-head">
            <Wordmark />
          </div>
          <div className="auth-info">{checkingExisting ? 'Checking your workspace…' : 'Loading your account…'}</div>
        </div>
      </div>
    );
  }

  if (requiresInvitation) {
    return (
      <div className="auth-page">
        <div className="auth-shell">
          <div className="auth-head">
            <Wordmark />
          </div>
          <div className="auth-card">
            <h2>You need an invitation</h2>
            <p>
              Transformax is invitation-only during private beta. This email
              {user?.email ? <> ({user.email})</> : ''} hasn't been approved to create a new
              organisation yet.
            </p>
            <div className="auth-trial-note" style={{ marginTop: 16 }}>
              <a className="auth-inline-link" href="/#invite">Request an invitation</a> and we'll follow up once it's reviewed.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <div className="auth-head">
          <Wordmark />
        </div>

        <form className="auth-card" onSubmit={handleSubmit}>
          <h2>Set up your organisation</h2>
          <p>
            You will be the <strong>admin</strong> for this organisation. Later, only programmes and projects
            for this organisation will be visible to your team (enforced by Row Level Security once your Auth0
            token is trusted by Supabase).
          </p>

          <label>Your name</label>
          <input
            value={fullName}
            onChange={(ev) => setFullName(ev.target.value)}
            placeholder="Jane Smith"
            autoComplete="name"
          />

          <label>Organisation</label>
          <input
            value={orgName}
            onChange={(ev) => setOrgName(ev.target.value)}
            placeholder="Acme Group"
            autoComplete="organization"
          />

          <label>Plan</label>
          <select value={plan} onChange={(ev) => setPlan(ev.target.value)}>
            {PLANS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>

          {user?.email && (
            <div className="auth-info" style={{ marginTop: 8 }}>Signed in as {user.email}</div>
          )}

          {error && <div className="form-error auth-error" role="alert">{error}</div>}

          <button type="submit" className="btn-primary ob-full" style={{ marginTop: 16 }} disabled={submitting}>
            {submitting ? 'Creating…' : 'Create organisation & continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
